import { useCreateAutomationTaskTemplateFromCandidateBatchMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useModalV2 } from 'common/modal/hooks/useModalV2';
import Yup from 'utils/validation';
import { useLoadingOverlay } from 'services/loading/hooks';
import { useToast } from 'services/toast/hooks';
import { ToastType } from 'services/toast/types';
import { Paths } from 'services/navigation/paths';
import { RouterLink } from 'common/ui/RouterLink';
import { FailedMessageWithHref } from 'common/ui/FailedMessageWithHref';
import { Typography } from '@mui/material';

export const useAnalyseDetailsCreateTemplate = () => {
  const [createAutomationTaskTemplateFromCandidateBatchMutation] =
    useCreateAutomationTaskTemplateFromCandidateBatchMutation();

  const { openModal } = useModalV2();
  const { showLoadingOverlay, hideLoadingOverlay } = useLoadingOverlay();
  const { showToast } = useToast();

  const onCreateTemplateClick = ({
    title,
    candidateBatchId
  }: {
    title: string;
    candidateBatchId: string;
  }) => {
    openModal({
      title: 'Create template?',
      subtitle:
        'Use rules and actions from this analysis to create a new template',
      width: '380px',
      form: {
        schema: Yup.object().shape({
          templateName: Yup.string().required('Template name is required')
        }),
        fields: {
          templateName: {
            type: 'text',
            label: 'Template Name'
          }
        },
        defaultValues: {
          templateName: `${title} template`
        }
      },
      primaryButtonText: 'Confirm',
      onPrimaryAction: async (values) => {
        try {
          showLoadingOverlay();

          if (!candidateBatchId) {
            showToast({
              message: 'Something went wrong',
              type: ToastType.Error
            });
            return;
          }

          await createAutomationTaskTemplateFromCandidateBatchMutation({
            variables: {
              input: {
                title: values.templateName,
                candidateBatchId: candidateBatchId
              }
            }
          });

          showToast({
            title: 'Template created',
            message: (
              <Typography variant="body2" mt={1} color="text.primary">
                View this new template on the{' '}
                <RouterLink to={Paths.ANALYSE_TEMPLATES}>
                  template listing screen.
                </RouterLink>
              </Typography>
            ),
            type: ToastType.Success
          });
        } catch (error) {
          showToast({
            title: 'Something went wrong',
            message: (
              <FailedMessageWithHref
                failedMessage=""
                emailSubject="Failed to create template"
              />
            ),
            type: ToastType.Error
          });
        } finally {
          hideLoadingOverlay();
        }
      }
    });
  };

  return {
    onCreateTemplateClick
  };
};
